export type DecisionRow = {
  id: string;
  type?: string;
  scope?: string;
  value?: string;
  scenario?: string;
  until?: string;
  duration?: string;
  origin?: string;
  reason?: string;
};

export type DecisionListType = "ban" | "captcha" | "whitelist" | "all";

export type DecisionListRequest = {
  limit?: number;
  type?: DecisionListType;
  ip?: string;
  scope?: "ip" | "range" | "country";
  origins?: string;
  scenario?: string;
};

export type BouncerRow = {
  name: string;
  type?: string;
  version?: string;
  ipAddress?: string;
  lastPull?: string;
  updatedAt?: string;
  revoked?: boolean;
};

export function buildDecisionsQueryString(params: DecisionListRequest): string {
  const limit = params.limit ?? 100;
  const type = params.type ?? "ban";
  const sp = new URLSearchParams();
  sp.set("limit", String(limit));
  sp.set("type", type);
  const ip = params.ip?.trim();
  if (ip) sp.set("ip", ip);
  if (params.scope) sp.set("scope", params.scope);
  const origins = params.origins?.trim();
  if (origins) sp.set("origins", origins);
  const scenario = params.scenario?.trim();
  if (scenario) sp.set("scenario", scenario);
  return sp.toString();
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export async function apiLogin(username: string, password: string): Promise<Response> {
  return fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username, password }),
  });
}

export async function apiLogout(): Promise<void> {
  await fetch("/api/logout", { method: "POST", credentials: "include" });
}

export async function apiListDecisions(params: DecisionListRequest = {}): Promise<DecisionRow[]> {
  const qs = buildDecisionsQueryString(params);
  const res = await fetch(`/api/decisions?${qs}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Failed to load decisions (${res.status})`);
  }
  const body = (await parseJson(res)) as unknown;
  if (!Array.isArray(body)) throw new Error("Unexpected decisions payload");
  return body as DecisionRow[];
}

export async function apiListBouncers(): Promise<BouncerRow[]> {
  const res = await fetch("/api/bouncers", { credentials: "include" });
  if (!res.ok) {
    throw new Error(`Failed to load bouncers (${res.status})`);
  }
  const body = (await parseJson(res)) as unknown;
  if (!Array.isArray(body)) throw new Error("Unexpected bouncers payload");
  return body as BouncerRow[];
}

export async function apiDeleteDecision(id: string): Promise<void> {
  const res = await fetch(`/api/decisions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (res.status === 204) return;
  const err = (await parseJson(res)) as { error?: string } | null;
  throw new Error(typeof err?.error === "string" ? err.error : `Unban failed (${res.status})`);
}

export async function apiWhitelist(body: {
  ip: string;
  duration?: string;
  reason?: string;
}): Promise<void> {
  const res = await fetch("/api/whitelist", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (res.status === 201 || res.status === 200) return;
  const err = (await parseJson(res)) as { error?: string } | null;
  throw new Error(typeof err?.error === "string" ? err.error : `Whitelist failed (${res.status})`);
}

export async function apiBan(body: { ip: string; duration?: string; reason?: string }): Promise<void> {
  const res = await fetch("/api/bans", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (res.status === 201 || res.status === 200) return;
  const err = (await parseJson(res)) as { error?: string } | null;
  throw new Error(typeof err?.error === "string" ? err.error : `Ban failed (${res.status})`);
}
